import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { ArrowLeft, Save, Plus, Trash2, FileText, Layers } from 'lucide-react';
import useSensitiveDataAck from '../hooks/useSensitiveDataAck';
import SensitiveDataDisclaimerModal from '../components/SensitiveDataDisclaimerModal';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const QUESTION_TYPES = [
  { value: 'text',      label: 'Short text' },
  { value: 'textarea',  label: 'Long text' },
  { value: 'select',    label: 'Dropdown' },
  { value: 'checkbox',  label: 'Checkboxes (multi-select)' },
  { value: 'yes_no',    label: 'Yes / No' },
  { value: 'date',      label: 'Date' },
  { value: 'signature', label: 'Signature pad' },
  { value: 'file',      label: 'File upload' },
];

/**
 * Form editor: name + description + intro / thank-you text + ordered
 * questions. Saves through the existing CRUD endpoints. The sensitive-data
 * disclaimer fires on the first save by an unacknowledged admin since
 * intake forms with health questions are explicitly listed as a sensitive
 * feature in the disclaimer wording.
 */
export default function FormEditor() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Attach-to-service state: list of all services + the set of currently
  // attached service_ids. Persisted via a single PUT when the admin
  // toggles a checkbox — no separate Save button so the UX matches the
  // other quick-toggle affordances in the editor.
  const [services, setServices] = useState([]);
  const [attachedIds, setAttachedIds] = useState(new Set());
  const [attachSaving, setAttachSaving] = useState(false);

  const ack = useSensitiveDataAck();
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  const authHeader = useCallback(() => ({
    headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [formRes, servicesRes, attachedRes] = await Promise.all([
        axios.get(`${API_URL}/forms/${id}`, authHeader()),
        axios.get(`${API_URL}/services`, authHeader()),
        axios.get(`${API_URL}/forms/${id}/attachments`, authHeader()),
      ]);
      setForm(formRes.data);
      setServices(servicesRes.data || []);
      setAttachedIds(new Set((attachedRes.data || []).map((a) => a.service_id)));
    } catch (e) {
      toast.error('Failed to load form');
      navigate('/forms');
    } finally {
      setLoading(false);
    }
  }, [id, authHeader, navigate]);

  useEffect(() => { load(); }, [load]);

  const toggleAttachment = async (serviceId) => {
    const next = new Set(attachedIds);
    if (next.has(serviceId)) next.delete(serviceId);
    else next.add(serviceId);
    setAttachedIds(next);
    setAttachSaving(true);
    try {
      await axios.put(`${API_URL}/forms/${id}/attachments`, {
        service_ids: Array.from(next),
      }, authHeader());
    } catch (e) {
      toast.error('Failed to update attachments');
      // Roll back on failure so the UI doesn't lie about persisted state.
      setAttachedIds(attachedIds);
    } finally {
      setAttachSaving(false);
    }
  };

  const performSave = async () => {
    if (!form?.name?.trim()) {
      toast.error('Form name is required');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/forms/${id}`, {
        name: form.name.trim(),
        description: form.description || '',
        intro_text: form.intro_text || '',
        thank_you_text: form.thank_you_text || '',
        is_active: !!form.is_active,
        requires_signature: !!form.requires_signature,
      }, authHeader());
      toast.success('Form saved');
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!ack.loading && !ack.acknowledged) {
      setShowDisclaimer(true);
      return;
    }
    await performSave();
  };

  // ─── Question handlers ──────────────────────────────────────────────

  const addQuestion = async () => {
    const label = window.prompt('Question label');
    if (!label || !label.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/forms/${id}/questions`, {
        label: label.trim(),
        type: 'text',
        is_required: false,
      }, authHeader());
      setForm(res.data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to add question');
    }
  };

  const updateQuestion = async (q, patch) => {
    try {
      const res = await axios.put(`${API_URL}/forms/${id}/questions/${q.id}`, patch, authHeader());
      setForm(res.data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to update question');
    }
  };

  const deleteQuestion = async (q) => {
    if (!window.confirm(`Delete question "${q.label}"?`)) return;
    try {
      const res = await axios.delete(`${API_URL}/forms/${id}/questions/${q.id}`, authHeader());
      setForm(res.data);
    } catch (e) {
      toast.error('Failed to delete question');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }
  if (!form) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      {showDisclaimer && (
        <SensitiveDataDisclaimerModal
          featureName="custom intake / consent forms"
          onAcknowledge={async () => {
            await ack.acknowledge();
            await performSave();
          }}
          onClose={() => setShowDisclaimer(false)}
        />
      )}

      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/forms')}
              className="p-2 hover:bg-gray-200 rounded-lg"
              title="Back to forms"
            >
              <ArrowLeft size={24} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
                <FileText className="text-primary-600" size={26} />
                <span>Edit form</span>
              </h1>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Save size={16} />
            <span>{saving ? 'Saving…' : 'Save form'}</span>
          </button>
        </div>

        {/* Form metadata */}
        <div className="card space-y-4 mb-6">
          <div>
            <label className="label">Form name *</label>
            <input
              type="text"
              className="input"
              value={form.name || ''}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Vaccination records, PAR-Q, Tattoo consent"
              maxLength={200}
            />
          </div>
          <div>
            <label className="label">Description (admin-only)</label>
            <input
              type="text"
              className="input"
              value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Internal note about when to use this form"
            />
          </div>
          <div>
            <label className="label">Intro text (shown to client at top of form)</label>
            <textarea
              rows="3"
              className="input"
              value={form.intro_text || ''}
              onChange={(e) => setForm({ ...form, intro_text: e.target.value })}
              placeholder="Optional. Use this to explain the form's purpose, set expectations, or include legal language."
            />
          </div>
          <div>
            <label className="label">Thank-you text (shown after submission)</label>
            <textarea
              rows="2"
              className="input"
              value={form.thank_you_text || ''}
              onChange={(e) => setForm({ ...form, thank_you_text: e.target.value })}
              placeholder="Optional. e.g. 'Thank you — we received your intake and will see you at your appointment.'"
            />
          </div>
          <div className="flex items-center space-x-6">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <span className="text-sm text-gray-700 font-medium">Active</span>
            </label>
          </div>
        </div>

        {/* Attach to services */}
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-gray-900 flex items-center space-x-2">
              <Layers size={18} className="text-primary-600" />
              <span>Attach to services</span>
            </h2>
            {attachSaving && <span className="text-xs text-gray-500">Saving…</span>}
          </div>
          <p className="text-sm text-gray-600 mb-3">
            Clients who book any of the checked services will automatically receive this
            form as required intake. They'll see share links to complete it in their
            appointment confirmation.
          </p>
          {services.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">
              No services configured yet. Add services under Services Management first.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto border border-gray-200 rounded p-3">
              {services.map((s) => (
                <label
                  key={s.id}
                  className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={attachedIds.has(s.id)}
                    onChange={() => toggleAttachment(s.id)}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <span className="text-sm text-gray-800 truncate" title={s.name}>{s.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Questions */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-gray-900">Questions</h2>
            <button
              onClick={addQuestion}
              className="btn btn-secondary flex items-center space-x-1"
            >
              <Plus size={14} />
              <span>Add question</span>
            </button>
          </div>

          {form.questions.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              No questions yet. Add your first question above.
            </p>
          ) : (
            <div className="space-y-4">
              {form.questions.map((q, idx) => (
                <QuestionRow
                  key={q.id}
                  index={idx + 1}
                  question={q}
                  onChange={(patch) => updateQuestion(q, patch)}
                  onDelete={() => deleteQuestion(q)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function QuestionRow({ index, question, onChange, onDelete }) {
  const showOptions = question.type === 'select' || question.type === 'checkbox';
  const [optionsDraft, setOptionsDraft] = useState((question.options || []).join('\n'));

  // Re-sync when the question is reloaded (e.g. after save).
  useEffect(() => { setOptionsDraft((question.options || []).join('\n')); }, [question.id]);

  const commitOptions = () => {
    const next = optionsDraft.split('\n').map((s) => s.trim()).filter(Boolean);
    onChange({ options: next });
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <div className="flex items-start space-x-3">
        <div className="text-xs font-bold text-gray-400 bg-white border border-gray-200 rounded px-2 py-1 mt-1">
          Q{index}
        </div>
        <div className="flex-1 space-y-3">
          <input
            type="text"
            className="input"
            defaultValue={question.label}
            onBlur={(e) => { if (e.target.value.trim() !== question.label) onChange({ label: e.target.value.trim() }); }}
            placeholder="Question text"
            maxLength={500}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-700">Type</label>
              <select
                className="input"
                value={question.type}
                onChange={(e) => onChange({ type: e.target.value })}
              >
                {QUESTION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Placeholder (optional)</label>
              <input
                type="text"
                className="input"
                defaultValue={question.placeholder || ''}
                onBlur={(e) => { if (e.target.value !== (question.placeholder || '')) onChange({ placeholder: e.target.value }); }}
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">Help text (optional)</label>
            <input
              type="text"
              className="input"
              defaultValue={question.help_text || ''}
              onBlur={(e) => { if (e.target.value !== (question.help_text || '')) onChange({ help_text: e.target.value }); }}
            />
          </div>

          {showOptions && (
            <div>
              <label className="text-xs font-semibold text-gray-700">
                Options (one per line)
              </label>
              <textarea
                rows="3"
                className="input font-mono text-sm"
                value={optionsDraft}
                onChange={(e) => setOptionsDraft(e.target.value)}
                onBlur={commitOptions}
                placeholder={'Option 1\nOption 2\nOption 3'}
              />
            </div>
          )}

          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!question.is_required}
              onChange={(e) => onChange({ is_required: e.target.checked })}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <span className="text-sm text-gray-700">Required</span>
          </label>
        </div>
        <button
          onClick={onDelete}
          className="p-2 text-red-600 hover:bg-red-50 rounded"
          title="Delete question"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
}
